import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/accounting/journal` — the journal and the trial balance.
 *
 * The journal is evidence: entries are neither edited nor deleted here,
 * only reversed — and the reversal stays visible in the list next to what
 * opravovalo.
 */

const searchSchema = z.object({ year: z.number().optional() });

type Line = {
  id: string;
  amount: string;
  description: string | null;
  debitAccount: { code: string; name: string };
  creditAccount: { code: string; name: string };
};

type Entry = {
  id: string;
  number: number;
  bookedOn: string;
  description: string;
  reversesId: string | null;
  lines: Line[];
};

type TrialRow = {
  code: string;
  name: string;
  debit: string;
  credit: string;
  balance: string;
};

export const Route = createFileRoute("/_app/accounting/journal")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  loader: async ({ deps }) => {
    const query = deps.year ? `?year=${deps.year}` : "";
    try {
      const [journal, balance] = await Promise.all([
        api.get<{ year: number; closed: boolean; entries: Entry[] }>(
          `/api/accounting/journal${query}`,
        ),
        api.get<{
          rows: TrialRow[];
          totalDebit: string;
          totalCredit: string;
          balanced: boolean;
        }>(`/api/accounting/trial-balance${query}`),
      ]);
      return { journal, balance };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: JournalPage,
});

function money(amount: string): string {
  return formatNumber(Number(amount), undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function JournalPage() {
  const { journal, balance } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useTranslation();

  const seed = useApiMutation(() => api.post("/api/accounting/accounts/seed", {}), {
    onSuccess: () => router.invalidate(),
    errorMessage: () => t("accJournal.seedFailed"),
  });

  const reverse = useApiMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/api/accounting/journal/${id}/reverse`, { reason }),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accJournal.reverseFailed") },
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">
            {t("accJournal.title", { year: journal.year })}
          </h1>
          <p className="text-muted-foreground text-sm">{t("accJournal.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {journal.closed && <Badge variant="secondary">{t("accJournal.periodClosed")}</Badge>}
          <input
            type="number"
            defaultValue={journal.year}
            className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) =>
              navigate({
                to: "/accounting/journal",
                search: { year: Number(e.target.value) || undefined },
              })
            }
          />
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>{t("accJournal.trialBalance")}</CardTitle>
            <CardDescription>{t("accJournal.trialBalanceHint")}</CardDescription>
          </div>
          <Badge variant={balance.balanced ? "success" : "destructive"}>
            {balance.balanced ? t("accJournal.sidesAgree") : t("accJournal.sidesDisagree")}
          </Badge>
        </CardHeader>
        <CardContent>
          {balance.rows.length === 0 ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">{t("accJournal.nothingPosted")}</p>
              <Button variant="outline" disabled={seed.pending} onClick={() => seed.mutate()}>
                {seed.pending ? t("accJournal.creating") : t("accJournal.seed")}
              </Button>
              {seed.error && <p className="text-destructive text-sm">{seed.error}</p>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accJournal.account")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accJournal.name")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accJournal.debit")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accJournal.credit")}</th>
                  <th className="py-1 text-right font-medium">{t("accJournal.balance")}</th>
                </tr>
              </thead>
              <tbody>
                {balance.rows.map((row) => (
                  <tr key={row.code} className="border-t">
                    <td className="py-1 pr-4 font-mono text-xs">{row.code}</td>
                    <td className="py-1 pr-4">{row.name}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(row.debit)}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(row.credit)}</td>
                    <td className="py-1 text-right font-medium tabular-nums">
                      {money(row.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold">
                  <td className="py-1 pr-4" colSpan={2}>
                    {t("accJournal.total")}
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(balance.totalDebit)}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">
                    {money(balance.totalCredit)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("accJournal.journal")}</CardTitle>
          <CardDescription>
            {t("accJournal.entries", { count: journal.entries.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {journal.entries.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("accJournal.nothingPostedYet")}</p>
          ) : (
            <div className="space-y-3">
              {journal.entries.map((entry) => (
                <div key={entry.id} className="rounded-md border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-mono text-xs">
                        {t("accJournal.entryNo", { number: entry.number })}
                      </span>{" "}
                      <span className="text-muted-foreground text-xs">
                        <LocalDate value={entry.bookedOn} />
                      </span>{" "}
                      <span className="text-sm">{entry.description}</span>
                      {entry.reversesId && (
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          {t("accJournal.storno")}
                        </Badge>
                      )}
                    </div>
                    {!entry.reversesId && (
                      <button
                        type="button"
                        className="text-muted-foreground text-xs underline"
                        disabled={reverse.pending}
                        onClick={() => {
                          const reason = window.prompt(t("accJournal.reversePrompt"));
                          if (reason && reason.trim().length >= 2) {
                            reverse.mutate({ id: entry.id, reason: reason.trim() });
                          }
                        }}
                      >
                        {t("accJournal.reverse")}
                      </button>
                    )}
                  </div>
                  <table className="mt-2 w-full text-sm">
                    <tbody>
                      {entry.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="py-0.5 pr-4 font-mono text-xs">
                            {line.debitAccount.code} / {line.creditAccount.code}
                          </td>
                          <td className="py-0.5 pr-4 text-muted-foreground text-xs">
                            {line.description ??
                              `${line.debitAccount.name} → ${line.creditAccount.name}`}
                          </td>
                          <td className="py-0.5 text-right tabular-nums">{money(line.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
          {reverse.error && <p className="mt-2 text-destructive text-sm">{reverse.error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
